import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Grid,
  Paper,
  Avatar,
  Fade,
  Zoom,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
  Alert,
  LinearProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Close as CloseIcon,
  Add as AddIcon,
  LocalPharmacy as PharmacyIcon,
  CurrencyRupee as RupeeIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  CloudUpload as UploadIcon,
  LocalOffer as TagIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";

// Tag color mapping function
const getTagColor = (tag) => {
  const upperTag = tag.toUpperCase();
  const tagMap = {
    PWC: { border: "#dc2626", text: "#dc2626", bg: "#fef2f2" },
    DRUGS: { border: "#667eea", text: "#667eea", bg: "#eef2ff" },
    AYURVEDA: { border: "#059669", text: "#059669", bg: "#ecfdf5" },
    HOMEO: { border: "#764ba2", text: "#764ba2", bg: "#f3e8ff" },
    OTC: { border: "#ea580c", text: "#ea580c", bg: "#fff7ed" },
    PRESCRIPTION: { border: "#0891b2", text: "#0891b2", bg: "#ecfeff" },
  };
  return (
    tagMap[upperTag] || { border: "#64748b", text: "#64748b", bg: "#f3f4f6" }
  );
};

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 24,
    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
    boxShadow: "0 25px 80px rgba(0, 0, 0, 0.15)",
    overflow: "visible",
    maxWidth: 700,
    width: "100%",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(3),
  borderRadius: "24px 24px 0 0",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -10,
    right: 20,
    width: 20,
    height: 20,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "50%",
    opacity: 0.3,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 16,
    transition: "all 0.3s ease",
    backgroundColor: "#ffffff",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
    },
    "&.Mui-focused": {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 30px rgba(102, 126, 234, 0.2)",
    },
  },
  "& .MuiInputLabel-root": {
    fontWeight: 600,
  },
}));

const UploadCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  border: "2px dashed #e0e7ff",
  backgroundColor: "#f8faff",
  transition: "all 0.3s ease",
  cursor: "pointer",
  "&:hover": {
    borderColor: "#667eea",
    backgroundColor: "#f0f4ff",
    transform: "translateY(-4px)",
    boxShadow: "0 12px 30px rgba(102, 126, 234, 0.15)",
  },
}));

const TagChip = styled(Chip)(({ theme, isSelected }) => ({
  borderRadius: 12,
  fontWeight: 600,
  transition: "all 0.3s ease",
  background: isSelected
    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    : "#f1f5f9",
  color: isSelected ? "#ffffff" : "#64748b",
  border: `2px solid ${isSelected ? "#667eea" : "#e2e8f0"}`,
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.1)",
    background: isSelected
      ? "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)"
      : "#ffffff",
  },
}));

const ActionButton = styled(Button)(({ theme, variant: buttonVariant }) => ({
  borderRadius: 16,
  padding: "12px 32px",
  fontWeight: 700,
  fontSize: "1rem",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 12px 30px rgba(0, 0, 0, 0.2)",
  },
}));

const FloatingActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: "16px 32px",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  fontWeight: 700,
  fontSize: "1.1rem",
  textTransform: "none",
  boxShadow: "0 12px 30px rgba(102, 126, 234, 0.4)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
    transform: "translateY(-4px)",
    boxShadow: "0 16px 40px rgba(102, 126, 234, 0.5)",
  },
}));

const AddDrugModal = ({ baseUrl = "https://api.medstown.com" }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newTag, setNewTag] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    price: "",
    short_composition: "",
    pack_size_label: "",
    image_url: "",
    tags: [],
  });

  const predefinedTags = [
    "DRUGS",
    "PWC",
    "AYURVEDA",
    "HOMEO",
    "OTC",
    "PRESCRIPTION",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tag],
      }));
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 80 * 1024) {
      alert("Image must be less than 80KB.");
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    setUploading(true);
    setUploadProgress(0);

    // Simulate upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 100);

    try {
      const response = await fetch(`${baseUrl}/api/medshubs/upload`, {
        method: "POST",
        body: formDataUpload,
      });

      const data = await response.json();
      const uploadedUrl = data?.url;

      if (uploadedUrl) {
        setFormData((prev) => ({ ...prev, image_url: uploadedUrl }));
        setUploadProgress(100);
      }
    } catch (err) {
      console.error("Image upload failed:", err);
      alert("Image upload failed.");
    } finally {
      clearInterval(progressInterval);
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 500);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const dataToSend = {
        ...formData,
        price: parseFloat(formData.price),
      };

      const response = await fetch(`${baseUrl}/api/medshubs/add-drug`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          closeModal();
        }, 2000);
      } else {
        throw new Error("Failed to add drug");
      }
    } catch (error) {
      console.error("Error adding drug:", error);
      alert("Error adding drug. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      name: "",
      price: "",
      short_composition: "",
      pack_size_label: "",
      image_url: "",
      tags: [],
    });
    setNewTag("");
    setShowSuccess(false);
  };

  return (
    <Box>
      {/* Floating Action Button */}
      <FloatingActionButton
        onClick={() => setIsModalOpen(true)}
        startIcon={<AddIcon />}
      >
        Add New Medicine
      </FloatingActionButton>

      {/* Advanced Modal */}
      <StyledDialog
        open={isModalOpen}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        TransitionComponent={Zoom}
        transitionDuration={400}
      >
        {/* Success Alert */}
        {showSuccess && (
          <Fade in={showSuccess}>
            <Alert
              icon={<CheckIcon fontSize="inherit" />}
              severity="success"
              sx={{
                position: "absolute",
                top: 20,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1000,
                borderRadius: 3,
                fontWeight: 600,
              }}
            >
              Medicine added successfully!
            </Alert>
          </Fade>
        )}

        {/* Header */}
        <StyledDialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                  width: 48,
                  height: 48,
                }}
              >
                <PharmacyIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Add New Medicine
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Fill in the details to add a new medicine to inventory
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={closeModal}
              sx={{
                color: "white",
                bgcolor: "rgba(255, 255, 255, 0.1)",
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </StyledDialogTitle>

        {/* Content */}
        <DialogContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Medicine Name */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <StyledTextField
                  fullWidth
                  label="Medicine Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PharmacyIcon sx={{ color: "#667eea" }} />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Enter medicine name..."
                />
              </Grid>

              {/* Price and Pack Size */}
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Price (₹)"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <RupeeIcon sx={{ color: "#059669" }} />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="₹ 0.00"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Pack Size"
                  name="pack_size_label"
                  value={formData.pack_size_label}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., 10 tablets, 100ml"
                />
              </Grid>

              {/* Composition */}
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Composition"
                  name="short_composition"
                  value={formData.short_composition}
                  onChange={handleInputChange}
                  required
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DescriptionIcon sx={{ color: "#8b5cf6", mt: -2 }} />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Enter medicine composition..."
                />
              </Grid>

              {/* Image Upload */}
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 600, color: "#374151" }}
                >
                  <ImageIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Product Image
                </Typography>

                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  style={{ display: "none" }}
                  id="image-upload"
                />

                <label htmlFor="image-upload">
                  <UploadCard sx={{ p: 3, textAlign: "center" }}>
                    <CardContent>
                      {uploading ? (
                        <Box>
                          <CircularProgress
                            size={40}
                            sx={{ color: "#667eea", mb: 2 }}
                          />
                          <Typography variant="body1" fontWeight={600}>
                            Uploading... {uploadProgress}%
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={uploadProgress}
                            sx={{ mt: 1, borderRadius: 2, height: 6 }}
                          />
                        </Box>
                      ) : (
                        <Box>
                          <UploadIcon
                            sx={{ fontSize: 48, color: "#667eea", mb: 1 }}
                          />
                          <Typography
                            variant="h6"
                            fontWeight={600}
                            color="#374151"
                          >
                            Upload Image
                          </Typography>
                          <Typography variant="body2" color="#64748b">
                            JPG/PNG only, Max 80KB
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </UploadCard>
                </label>

                {formData.image_url && (
                  <Fade in={Boolean(formData.image_url)}>
                    <Card sx={{ mt: 2, p: 2, borderRadius: 3 }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <img
                          src={formData.image_url}
                          alt="Preview"
                          style={{
                            width: 80,
                            height: 80,
                            borderRadius: 12,
                            objectFit: "cover",
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="success.main"
                          fontWeight={600}
                        >
                          ✅ Image uploaded successfully!
                        </Typography>
                      </Box>
                    </Card>
                  </Fade>
                )}
              </Grid>

              {/* Tags Section */}
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 600, color: "#374151" }}
                >
                  <TagIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Medicine Tags
                </Typography>

                {/* Predefined Tags */}
                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                  {predefinedTags.map((tag, index) => {
                    const tagColor = getTagColor(tag);
                    return (
                      <Chip
                        key={tag}
                        label={tag}
                        isSelected={formData.tags.includes(tag)}
                        onClick={() => handleAddTag(tag)}
                        clickable
                        sx={{
                          borderRadius: 2,
                          borderColor: tagColor.border,
                          color: tagColor.text,
                          backgroundColor: tagColor.bg,
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          "& .MuiChip-deleteIcon": {
                            color: tagColor.text,
                          },
                        }}
                      />
                    );
                  })}
                </Box>

                {/* Custom Tag Input */}
                <Box display="flex" gap={2} mb={2}>
                  <StyledTextField
                    fullWidth
                    size="small"
                    label="Add Custom Tag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value.toUpperCase())}
                    onKeyPress={(e) =>
                      e.key === "Enter" &&
                      (e.preventDefault(), handleAddTag(newTag))
                    }
                  />
                  <Button
                    variant="contained"
                    onClick={() => handleAddTag(newTag)}
                    sx={{ borderRadius: 2, px: 3 }}
                  >
                    Add
                  </Button>
                </Box>

                {/* Selected Tags */}
                {formData.tags.length > 0 && (
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      Selected Tags:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {formData.tags.map((tag, index) => {
                        const tagColor = getTagColor(tag);
                        return (
                          <Chip
                            key={index}
                            label={tag}
                            onDelete={() => handleRemoveTag(tag)}
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              borderColor: tagColor.border,
                              color: tagColor.text,
                              backgroundColor: tagColor.bg,
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              "& .MuiChip-deleteIcon": {
                                color: tagColor.text,
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Paper>
                )}
              </Grid>
            </Grid>
          </form>
        </DialogContent>

        {/* Actions */}
        <DialogActions sx={{ p: 4, gap: 2 }}>
          <ActionButton
            variant="outlined"
            onClick={closeModal}
            startIcon={<CancelIcon />}
            sx={{ flex: 1 }}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading || uploading}
            startIcon={
              isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            sx={{
              flex: 1,
              background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #047857 0%, #065f46 100%)",
              },
            }}
          >
            {isLoading ? "Adding Medicine..." : "Add Medicine"}
          </ActionButton>
        </DialogActions>
      </StyledDialog>
    </Box>
  );
};

export default AddDrugModal;
